import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User } from '../../types';

const TOKEN_KEY = 'sproutly_token';
const REFRESH_TOKEN_KEY = 'sproutly_refresh_token';
const USER_KEY = 'sproutly_user';
const ACTIVE_MEMBERSHIP_KEY = 'sproutly_active_membership';

// Load persisted auth state from localStorage
const loadAuthState = (): Partial<AuthState> => {
  try {
    const storedUser = localStorage.getItem(USER_KEY);
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (storedUser && storedToken) {
      const user = JSON.parse(storedUser);
      return {
        user: { ...user, memberships: user.memberships || [] },
        token: storedToken,
        refreshToken: storedRefreshToken,
        isAuthenticated: true,
      };
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.error('Failed to load auth state:', e);
    }
  }
  return {};
};

const persistedState = loadAuthState();

const rawMembershipIndex = parseInt(localStorage.getItem(ACTIVE_MEMBERSHIP_KEY) || '0', 10);
// Guard against a corrupted value (NaN/negative) so the active index is always
// a valid array position; useAuth additionally clamps it to the array length.
const persistedMembershipIndex =
  Number.isInteger(rawMembershipIndex) && rawMembershipIndex >= 0 ? rawMembershipIndex : 0;

const initialState: AuthState = {
  user: persistedState.user || null,
  token: persistedState.token || null,
  refreshToken: persistedState.refreshToken || null,
  isAuthenticated: persistedState.isAuthenticated || false,
  loading: false,
  error: null,
  activeMembershipIndex: persistedMembershipIndex,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{ user: User; token: string; refreshToken?: string | null }>
    ) => {
      state.loading = false;
      // Ensure user always has memberships array (defensive)
      const user = {
        ...action.payload.user,
        memberships: action.payload.user.memberships || [],
      };
      state.user = user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken ?? null;
      state.isAuthenticated = true;
      state.error = null;
      state.activeMembershipIndex = 0;
      // Persist to localStorage
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      localStorage.setItem(TOKEN_KEY, action.payload.token);
      if (action.payload.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, action.payload.refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
      localStorage.setItem(ACTIVE_MEMBERSHIP_KEY, '0');
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },
    // Password step succeeded but the account requires a second factor —
    // this is not a failure and not a success (no tokens yet), so it gets
    // its own reducer rather than overloading loginFailure with an empty
    // error string. The login page owns the challenge/session state locally;
    // this only stops the "Signing in..." spinner.
    mfaChallengeIssued: (state) => {
      state.loading = false;
      state.error = null;
    },
    // Swap in fresh tokens after a successful refresh, without resetting
    // user / membership state. Called by the API layer's 401 interceptor.
    tokenRefreshed: (
      state,
      action: PayloadAction<{ token: string; refreshToken?: string | null }>
    ) => {
      state.token = action.payload.token;
      localStorage.setItem(TOKEN_KEY, action.payload.token);
      // Cognito sometimes rotates the refresh token; persist the new one if provided.
      if (action.payload.refreshToken) {
        state.refreshToken = action.payload.refreshToken;
        localStorage.setItem(REFRESH_TOKEN_KEY, action.payload.refreshToken);
      }
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.activeMembershipIndex = 0;
      // Clear localStorage
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      // Clear the active membership so the next user doesn't inherit the
      // previous user's selected context index.
      localStorage.removeItem(ACTIVE_MEMBERSHIP_KEY);
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      // Persist so a page refresh sees the same memberships array (and same
      // ordering) that activeMembershipIndex was resolved against. Without
      // this, Layout's /session/contexts sync can reorder memberships in
      // Redux, remap the index to match, and then a refresh reloads the
      // OLD login-time ordering from localStorage while the persisted index
      // still targets the reordered slot — pointing the user at the wrong
      // location/role until they switch manually.
      try {
        localStorage.setItem(USER_KEY, JSON.stringify(action.payload));
      } catch {
        // Storage disabled / quota — non-fatal, in-memory state still correct.
      }
    },
    switchMembership: (state, action: PayloadAction<number>) => {
      state.activeMembershipIndex = action.payload;
      localStorage.setItem(ACTIVE_MEMBERSHIP_KEY, String(action.payload));
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  mfaChallengeIssued,
  tokenRefreshed,
  logout,
  setUser,
  switchMembership,
} = authSlice.actions;

export default authSlice.reducer;
